import {
  mermaidOutputProblem,
  reportMermaidFailure,
} from "../../jan/utils/mermaidReporting";

// Pattern matches the same fenced ```mermaid blocks the public-Jan
// renderer recognises; kept identical so on-page diagrams and replaced
// markdown stay in lock-step.
const MERMAID_RE = /```mermaid\n([\s\S]*?)```/g;

export interface NormalizedSvg {
  svg: string;
  width: number;
  height: number;
}

/**
 * Mermaid emits SVGs with `width="100%"` and an inline `max-width` so the
 * on-page element scales to its container. For an off-page render (the
 * server-side Puppeteer pipeline that turns this markdown into a PDF) we
 * need explicit pixel dimensions taken from the viewBox, otherwise the
 * server's marked → HTML → Puppeteer flow has no concept of the diagram's
 * intrinsic size and the rendered diagram inherits whatever container it
 * happens to land in.
 *
 * Mirrors `normalizeSvg` in MermaidDiagramModal.tsx — kept duplicated
 * intentionally so the public-Jan export path has no dependency on the
 * in-app jan UI tree.
 */
export function normalizeSvgForCapture(svgString: string): NormalizedSvg {
  const viewBoxMatch = svgString.match(
    /viewBox=["'][\s]*(\S+)\s+(\S+)\s+(\S+)\s+(\S+)["']/
  );
  if (!viewBoxMatch) return { svg: svgString, width: 0, height: 0 };

  const w = parseFloat(viewBoxMatch[3]);
  const h = parseFloat(viewBoxMatch[4]);
  if (w <= 0 || h <= 0) return { svg: svgString, width: 0, height: 0 };

  let normalized = svgString
    .replace(/(<svg[^>]*)\bwidth=["'][^"']*["']/, `$1width="${w}"`)
    .replace(/(<svg[^>]*)\bheight=["'][^"']*["']/, `$1height="${h}"`);

  normalized = normalized.replace(
    /(<svg[^>]*style=["'][^"']*)max-width:\s*[^;"']+;?/,
    "$1"
  );

  return { svg: normalized, width: w, height: h };
}

/**
 * Encode an SVG string as a `data:image/svg+xml;base64,…` URI.
 *
 * `btoa` only accepts Latin-1; SVG label text routinely contains
 * non-ASCII characters (Unicode arrows, em-dashes, accented vowels in
 * clinical terms), so we round-trip through TextEncoder first.
 */
export function svgToBase64DataUri(svgString: string): string {
  const utf8 = new TextEncoder().encode(svgString);
  let binary = "";
  for (let i = 0; i < utf8.length; i++) {
    binary += String.fromCharCode(utf8[i]);
  }
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

/**
 * Replace each fenced ```mermaid block in `markdown` with a markdown image
 * whose URL is a base64 data URI of the already-rendered SVG taken from
 * the corresponding `.mermaid-diagram` element under `root`. The image
 * gets the title `mermaid-diagram` so the backend PDF renderer can tag it
 * with `class="mermaid-diagram"` and apply the per-class sizing rules
 * defined in pdf-styles.css (no max-height, page-break-inside: avoid).
 *
 * Order matters: the i-th `.mermaid-diagram` element corresponds to the
 * i-th ```mermaid block, because `PublicMessageBubble` renders segments
 * in document order.
 *
 * Returns the markdown unchanged when there are no mermaid blocks or no
 * root element. Individual blocks that can't be captured (missing SVG,
 * missing or zero-area viewBox) fall back to the original code fence so
 * the user still sees something rather than a broken image link.
 */
export function transformMermaidBlocksToInlineSvg(
  markdown: string,
  root: HTMLElement | null
): string {
  const blocks = [...markdown.matchAll(MERMAID_RE)];
  if (blocks.length === 0 || !root) return markdown;

  const diagramEls = Array.from(
    root.querySelectorAll<HTMLElement>(".mermaid-diagram")
  );

  const serializer = new XMLSerializer();
  const dataUris: (string | null)[] = blocks.map((_, i) => {
    const wrapper = diagramEls[i];
    if (!wrapper) {
      reportMermaidFailure("public-export", "missing-diagram");
      return null;
    }
    const svgEl = wrapper.querySelector("svg");
    if (!svgEl) {
      reportMermaidFailure("public-export", "missing-diagram");
      return null;
    }
    // XMLSerializer preserves the SVG and foreignObject xmlns declarations
    // that Chromium needs to render a standalone SVG loaded via `<img src>`.
    // `svgEl.outerHTML` uses HTML serialization, which silently drops those
    // namespaces and produces a broken image in the rendered PDF.
    let rawSvg = serializer.serializeToString(svgEl);
    // Defensive: some serializers omit the SVG xmlns when the element was
    // parsed from HTML rather than XML. Without it the standalone SVG is
    // unparseable.
    if (
      !/\bxmlns\s*=\s*["']http:\/\/www\.w3\.org\/2000\/svg["']/.test(rawSvg)
    ) {
      rawSvg = rawSvg.replace(
        /^<svg\b/,
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }
    // Mermaid uses <foreignObject> with xhtml children for HTML labels.
    // If the xhtml namespace got dropped during parse/serialize, foreignObject
    // children render as empty boxes in standalone SVG.
    if (
      rawSvg.includes("<foreignObject") &&
      !/xmlns:xhtml\s*=\s*["']http:\/\/www\.w3\.org\/1999\/xhtml["']/.test(
        rawSvg
      )
    ) {
      rawSvg = rawSvg.replace(
        /^<svg\b/,
        '<svg xmlns:xhtml="http://www.w3.org/1999/xhtml"'
      );
    }
    const displayProblem = mermaidOutputProblem(rawSvg, true);
    if (displayProblem) {
      reportMermaidFailure("public-export", displayProblem, {
        svgLength: rawSvg.length,
      });
      return null;
    }
    const { svg, width, height } = normalizeSvgForCapture(rawSvg);
    if (width <= 0 || height <= 0) {
      reportMermaidFailure("public-export", "invalid-svg", {
        svgLength: rawSvg.length,
      });
      return null;
    }
    return svgToBase64DataUri(svg);
  });

  // Splice back-to-front so earlier match indices stay valid.
  let out = markdown;
  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i];
    const dataUri = dataUris[i];
    const start = block.index ?? 0;
    const end = start + block[0].length;
    const replacement = dataUri
      ? `![Mermaid diagram](${dataUri} "mermaid-diagram")`
      : block[0];
    out = out.slice(0, start) + replacement + out.slice(end);
  }
  return out;
}
